import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * 分頁組件
 * @param {Object} props
 * @param {number} props.currentPage - 當前頁碼
 * @param {number} props.totalItems - 總項目數
 * @param {number} props.perPage - 每頁項目數
 * @param {Function} props.onPageChange - 頁碼變更回調函數
 */
const Pagination = ({
    currentPage,
    totalItems,
    perPage,
    onPageChange
}) => {
    const totalPages = Math.ceil(totalItems / perPage);

    // 如果總頁數少於等於1，不顯示分頁
    if (totalPages <= 1) {
        return null;
    }

    // 計算要顯示的頁碼範圍
    const getPageNumbers = () => {
        const maxPageButtons = 5; // 最多顯示的頁碼按鈕數
        const pageNumbers = [];

        if (totalPages <= maxPageButtons) {
            // 如果總頁數小於等於最大顯示數，直接顯示所有頁碼
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // 如果總頁數大於最大顯示數，需要選擇性顯示
            if (currentPage <= 3) {
                // 當前頁在前三頁
                for (let i = 1; i <= 4; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // 當前頁在後三頁
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                // 當前頁在中間
                pageNumbers.push(1);
                pageNumbers.push('...');
                pageNumbers.push(currentPage - 1);
                pageNumbers.push(currentPage);
                pageNumbers.push(currentPage + 1);
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            }
        }

        return pageNumbers;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
                顯示第 {(currentPage - 1) * perPage + 1} 到 {Math.min(currentPage * perPage, totalItems)} 項，共 {totalItems} 項
            </div>

            <div className="flex space-x-1">
                {/* 上一頁按鈕 */}
                <button
                    onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md ${currentPage === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>

                {/* 頁碼按鈕 */}
                {pageNumbers.map((page, index) => (
                    <button
                        key={index}
                        onClick={() => typeof page === 'number' && onPageChange(page)}
                        disabled={page === '...' || page === currentPage}
                        className={`px-3 py-1 rounded-md ${page === currentPage
                                ? 'bg-blue-500 text-white'
                                : page === '...'
                                    ? 'text-gray-400 cursor-default'
                                    : 'text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        {page}
                    </button>
                ))}

                {/* 下一頁按鈕 */}
                <button
                    onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md ${currentPage === totalPages
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
};

export default Pagination;